import VirtualBackgroundExtension, { IVirtualBackgroundProcessor } from 'agora-extension-virtual-background';
import AgoraRTC, { type ICameraVideoTrack, ILocalVideoTrack } from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger';
import { AGORA_ENCODER_CONFIG } from '@studio/constants';

/* eslint-disable no-unused-vars */
export enum BACKGROUND_OPTIONS {
  BLUR = 'Blur',
  CUSTOM = 'Custom',
  NONE = 'None'
}

const LOG = new StudioLogger('agoraVirtualBackgroundLogger');

const extension = new VirtualBackgroundExtension();

let processor: IVirtualBackgroundProcessor | null = null;

/**
 * Configuration for blur virtual background effect
 */
interface BlurVFXConfig {
  type: 'blur';
  blurDegree: number;
}

/**
 * Configuration for custom image virtual background effect
 */
interface ImageVFXConfig {
  type: 'img';
  source: HTMLImageElement;
}

/**
 * Configuration for color virtual background effect
 */
interface ColorVFXConfig {
  type: 'color';
  color: string;
}

/**
 * Type for the transformed video input
 */
interface TransformedVideoInput {
  transformedVideoTrack: ICameraVideoTrack;
  device: string;
}

/**
 * Type for all possible VFX configurations
 */
type VFXConfig = BlurVFXConfig | ImageVFXConfig | ColorVFXConfig;

type OperatingSystem = 'Windows' | 'MacOS' | 'Linux' | 'Android' | 'iOS' | 'Unknown';

/**
 * Helper to detect operating system from user agent
 */
export const detectOS = (): OperatingSystem => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('win') !== -1) return 'Windows';
  if (userAgent.indexOf('mac') !== -1) return 'MacOS';
  if (userAgent.indexOf('linux') !== -1) return 'Linux';
  if (userAgent.indexOf('android') !== -1) return 'Android';
  if (userAgent.indexOf('ios') !== -1 || (userAgent.indexOf('mac') !== -1 && navigator.maxTouchPoints > 0))
    return 'iOS';
  return 'Unknown';
};

/**
 * Get appropriate WASM file path based on browser and OS
 */
export const getWasmPath = (): string => {
  // Use the latest version of the WASM file from CDN that matches our dependency
  const baseUrl: string = 'https://cdn.jsdelivr.net/npm/agora-extension-virtual-background@2.1.0/wasms';

  // For Safari and other browsers
  const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const os: OperatingSystem = detectOS();

  console.log('Detected OS and browser', { os, isSafari });

  if (isSafari) {
    return `${baseUrl}/agora-wasm-safari.wasm`;
  }

  return `${baseUrl}/agora-wasm.wasm`;
};

const wasmPath: string = getWasmPath();

/**
 * Checks device compatibility for the virtual background feature
 * @returns {boolean}
 * @throws {Error} When virtual background is not supported

 */
const checkDeviceCompatibility = (): boolean => {
  const isSupported = extension.checkCompatibility();
  if (!isSupported) {
    throw new Error(`Virtual background is not supported`);
  }

  return isSupported;
};

/**
 * Initializes the virtual background extension
 * @returns {void}
 */
export const initializeVirtualBackground = async (): Promise<void> => {
  if (processor) return;
  try {
    checkDeviceCompatibility();
    AgoraRTC.registerExtensions([extension]);
    processor = extension.createProcessor();
    await processor.init(wasmPath);
    LOG.info('Virtual background processor initialized successfully');
  } catch (e) {
    processor = null;
    const error = e as Error;
    throw new Error(`Error initializing virtual background: ${error.message}`);
  }
};

/**
 * Creates a new transformed video device with background filter applied
 * @param videoTrack
 * @param videoFxConfig
 * @param videoInputId
 * @returns {Promise<TransformedVideoInput>}
 */
export const transformVideo = async (
  videoTrack: ICameraVideoTrack,
  videoFxConfig: VFXConfig,
  videoInputId: string
): Promise<{ transformedVideoTrack: ICameraVideoTrack; device: string }> => {
  await initializeVirtualBackground();

  if (!processor) {
    throw new Error('Virtual background processor is not initialized');
  }

  processor.setOptions(videoFxConfig);
  videoTrack.pipe(processor).pipe(videoTrack.processorDestination);
  await processor.enable();
  LOG.info('Video track transformed with virtual background');

  return {
    transformedVideoTrack: videoTrack,
    device: videoInputId
  };
};

/**
 * Temporarily disables virtual background but keeps processor for reuse
 * @returns {void}
 */
export const clearVirtualBackground = async (): Promise<void> => {
  if (!processor) {
    LOG.error('No processor found');
    return;
  }

  await processor.disable();
  processor = null;
  LOG.info('Clearing virtual background');
};

/**
 * Releases the extension resources that are no longer needed.
 */
export const releaseProcessor = (videoTrack: ILocalVideoTrack): void => {
  if (processor && videoTrack) {
    videoTrack.unpipe();
    processor.unpipe();
    processor.release();
    processor = null;
    LOG.info('Released virtual background processor');
  }
};

/**
 * Returns the configuration for the virtual background effect
 * @param filterType
 * @param backgroundImageURL
 * @returns {{type: string, blurDegree?: number, source?: HTMLImageElement}}
 */
export const getVFXConfig = (
  filterType: BACKGROUND_OPTIONS,
  backgroundImageURL: string = ''
): VFXConfig | undefined => {
  if (filterType === BACKGROUND_OPTIONS.BLUR) {
    return {
      type: 'blur',
      blurDegree: 3
    };
  }

  if (filterType === BACKGROUND_OPTIONS.CUSTOM) {
    if (!backgroundImageURL) {
      return {
        type: 'color',
        color: '#5C617C'
      };
    }

    const img = new Image();
    img.src = backgroundImageURL;
    return {
      type: 'img',
      source: img
    };
  }
  return undefined;
};

/**
 * Get video input based on option
 * @param option
 * @param transformedVideoInput
 * @param selectedVideoInputId
 * @param onTransformedVideoInputChange
 * @param backgroundUrl
 * @returns {Promise<ILocalVideoTrack>}
 */
export const getVideoInput = async (
  option: BACKGROUND_OPTIONS,
  transformedVideoInput: TransformedVideoInput | null,
  selectedVideoInputId: string,
  onTransformedVideoInputChange: (input: { transformedVideoTrack?: ICameraVideoTrack; device?: string }) => void,
  backgroundUrl = ''
): Promise<{ transformedVideoTrack?: ICameraVideoTrack; device?: string } | TransformedVideoInput> => {
  const videoTrack = await AgoraRTC.createCameraVideoTrack({
    cameraId: selectedVideoInputId,
    encoderConfig: AGORA_ENCODER_CONFIG
  });
  const videoFxConfig = getVFXConfig(option, backgroundUrl);

  const userHasBackground = option !== BACKGROUND_OPTIONS.NONE;

  let videoInput: { transformedVideoTrack?: ICameraVideoTrack; device?: string } = {
    device: selectedVideoInputId
  };

  const isFirstTimeBackgroundChange = userHasBackground && transformedVideoInput === null;

  const isUpdatingBackgroundFilter =
    userHasBackground && transformedVideoInput && transformedVideoInput?.device === selectedVideoInputId;

  const isChangingDevicesWithBackgroundFilter =
    userHasBackground && transformedVideoInput && transformedVideoInput?.device !== selectedVideoInputId;

  if (!processor) {
    throw new Error('Trying to apply virtual background without initializing the processor or invalid config');
  }

  if (isFirstTimeBackgroundChange && videoFxConfig) {
    videoInput = await transformVideo(videoTrack, videoFxConfig, selectedVideoInputId);
    onTransformedVideoInputChange(videoInput);
  } else if ((isChangingDevicesWithBackgroundFilter || isUpdatingBackgroundFilter) && videoFxConfig) {
    transformedVideoInput.transformedVideoTrack.close();
    processor.reset();
    videoInput = await transformVideo(videoTrack, videoFxConfig, selectedVideoInputId);
  }
  return videoInput || transformedVideoInput;
};
